import type { ReportType } from "./types";

export type VoiceCommand =
  | { kind: "report"; type: ReportType }
  | { kind: "present"; type?: ReportType }
  | { kind: "absent"; type?: ReportType }
  | { kind: "unknown" };
const camera = "מצלמה|מצלמת (?:ה)?(?:דרך|מהירות|רמזור)";
const names: [ReportType, string][] = [
  ["FIXED_CAMERA", `(?:${camera}) (?:ה)?קבועה`],
  ["MOBILE_CAMERA", `(?:${camera}) (?:ה)?(?:מתניידת|ניידת)`],
  ["POLICE", "משטרה|שוטר(?:ים)?|ניידת"],
  ["CAMERA", camera],
  ["SLIPPERY_ROAD", "כביש (?:חלק|מחליק)"],
  ["OBJECT_ON_ROAD", "(?:עצם|עצמים|חפץ|חפצים) (?:על (?:ה)?כביש|בכביש)"],
  ["ACCIDENT", "תאונה"],
  ["CLOSURE", "כביש חסום|חסימה|חסימת כביש"],
  ["TRAFFIC", "פקק|עומס תנועה"],
  ["HAZARD", "מפגע|מכשול|סכנה בדרך"],
];
const place = "(?: (?:כאן|פה|בדרך|לפניי|בצד הדרך))?";
const gone =
  "(?:כבר )?(?:לא קיים|לא קיימת|לא קיימים|לא קיימות|לא נמצא|לא נמצאת|לא נמצאים|לא נמצאות|נעלם|נעלמה|נעלמו|הסתיים|הסתיימה|הוסר|הוסרה|פנוי|פנויה)";
const commandPatterns = names.map(([type, name]) => {
  const subject = `(?:ה)?(?:${name})`;
  return {
    type,
    report: new RegExp(
      `^(?:(?:דווח(?:י)?(?: על)?|דיווח(?: על)?|יש(?: כאן| פה)?|ראיתי|כאן|פה|עדיין יש) )?${subject}${place}$`,
    ),
    absent: new RegExp(
      `^(?:(?:כבר אין|אין(?: יותר)?) ${subject}|${subject} ${gone})${place}$`,
    ),
    present: new RegExp(
      `^(?:(?:כן(?: עדיין)?(?: יש)?|עדיין(?: יש)?|נשאר(?:ה|ו)?) ${subject}|${subject} (?:עדיין|נשאר(?:ה|ו)?))${place}$`,
    ),
  };
});
const presentReply =
  /^(?:כן(?: עדיין(?: יש)?)?|עדיין(?: יש)?|נשאר(?:ה|ו)?)(?: (?:כאן|פה))?$/;
const absentReply = new RegExp(
  `^(?:לא|כבר אין|אין(?: יותר)?|כבר לא|${gone})${place}$`,
);
const slipperyCleared = new RegExp(
  `^(?:ה)?כביש (?:כבר )?לא (?:חלק|מחליק)${place}$`,
);

/** Match whole phrases: a keyword inside uncertain or unrelated speech is never a vote. */
export function parseVoiceCommand(
  input: string,
  hasQuestion = false,
): VoiceCommand {
  const text = input
    .normalize("NFKC")
    // Remove vowel/cantillation marks, but preserve Hebrew punctuation as word separators.
    .replace(/[\u0591-\u05bd\u05bf\u05c1-\u05c2\u05c4-\u05c5\u05c7]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text || text.length > 160 || /[?؟]/.test(input))
    return { kind: "unknown" };
  if (hasQuestion) {
    if (absentReply.test(text)) return { kind: "absent" };
    if (presentReply.test(text)) return { kind: "present" };
  }
  if (slipperyCleared.test(text))
    return { kind: "absent", type: "SLIPPERY_ROAD" };
  for (const { type, absent, present, report } of commandPatterns) {
    if (absent.test(text)) return { kind: "absent", type };
    if (hasQuestion && present.test(text)) return { kind: "present", type };
    if (report.test(text)) return { kind: "report", type };
  }
  return { kind: "unknown" };
}
